import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

/**
 * Sprint 5b.1 v2 — Phase C.0.3 : composant `Combobox` shadcn avec virtualisation.
 *
 * Objectif : picker autocomplete fluide sur de gros référentiels (~9366 codes CPV
 * dans le cas le plus volumineux).
 *
 * Implémentation :
 *   - Popover + Command (cmdk) pour l'UX recherche-clavier standard
 *   - cmdk gère le filtre fuzzy en interne quand on lui passe `value` sur chaque item
 *   - Virtualisation via @tanstack/react-virtual sur la liste filtrée (les options non
 *     visibles ne sont pas rendues dans le DOM)
 *   - Comparaison sur `value` (string), `label` affiché à l'utilisateur, `keywords`
 *     optionnel pour étendre le matching (ex. nom secteur en plus du code CPV)
 */

export interface ComboboxOption {
  value: string;
  label: string;
  keywords?: string[];
  /** Sous-titre affiché en gris à droite (ex. secteur d'un CPV) */
  hint?: string;
}

export interface ComboboxProps {
  options: ComboboxOption[];
  /** Valeur sélectionnée (ou undefined si vide) */
  value?: string;
  onChange: (value: string | undefined) => void;
  /** Label du trigger quand aucune valeur n'est sélectionnée */
  placeholder?: string;
  /** Placeholder de l'input de recherche */
  searchPlaceholder?: string;
  /** Message affiché si aucun résultat ne matche la recherche */
  emptyMessage?: string;
  /** Largeur du popover (default : largeur du trigger) */
  contentClassName?: string;
  triggerClassName?: string;
  disabled?: boolean;
  /** Hauteur estimée d'un item pour la virtualisation (px). Default 36 */
  itemHeight?: number;
  /** Hauteur max de la liste virtualisée (px). Default 300 */
  maxListHeight?: number;
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder = 'Sélectionner...',
  searchPlaceholder = 'Rechercher...',
  emptyMessage = 'Aucun résultat.',
  contentClassName,
  triggerClassName,
  disabled,
  itemHeight = 36,
  maxListHeight = 300,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const filtered = React.useMemo(() => {
    if (!search.trim()) return options;
    const needle = search.trim().toLowerCase();
    return options.filter((opt) => {
      const haystack = [opt.label, opt.value, ...(opt.keywords ?? [])]
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [options, search]);

  const parentRef = React.useRef<HTMLDivElement | null>(null);

  const rowVirtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => itemHeight,
    overscan: 8,
  });

  const selectedLabel = React.useMemo(() => {
    if (!value) return null;
    return options.find((o) => o.value === value)?.label ?? value;
  }, [options, value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal',
            !selectedLabel && 'text-muted-foreground',
            triggerClassName,
          )}
        >
          <span className="truncate text-left">
            {selectedLabel ?? placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('w-[--radix-popover-trigger-width] p-0', contentClassName)}
        align="start"
      >
        {/* shouldFilter={false} : on gère le filtrage côté virtualisation */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={setSearch}
          />
          {filtered.length === 0 ? (
            <CommandEmpty>{emptyMessage}</CommandEmpty>
          ) : (
            <CommandList>
              <div
                ref={parentRef}
                className="overflow-auto"
                style={{ height: Math.min(filtered.length * itemHeight + 8, maxListHeight) }}
              >
                <div
                  style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const opt = filtered[virtualRow.index];
                    if (!opt) return null;
                    const isSelected = value === opt.value;
                    return (
                      <CommandItem
                        key={opt.value}
                        value={opt.value}
                        onSelect={() => {
                          onChange(isSelected ? undefined : opt.value);
                          setOpen(false);
                          setSearch('');
                        }}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: `${virtualRow.size}px`,
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                        className="cursor-pointer"
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            isSelected ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                        <div className="flex-1 truncate">
                          <span className="font-mono text-xs text-muted-foreground mr-2">
                            {opt.value}
                          </span>
                          <span>{opt.label}</span>
                        </div>
                        {opt.hint && (
                          <span className="ml-2 text-xs text-muted-foreground truncate max-w-[40%]">
                            {opt.hint}
                          </span>
                        )}
                      </CommandItem>
                    );
                  })}
                </div>
              </div>
            </CommandList>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
